import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, CheckCircle2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { API_BASE } from "../lib/api";
import axios from "axios";
import RenderMarkdown from "../components/RenderMarkdown";
import useForceLightTheme from "../lib/useForceLightTheme";

/**
 * Public installation-terms signing page. Customer arrives here from the
 * booking confirmation email, reads the T&Cs, types their name, taps sign.
 * No auth — the URL's share_token is the shared secret.
 *
 * Renders markdown headings + paragraphs inline (small subset) so the shop
 * doesn't need to ship a full markdown lib to the browser.
 */
export default function PublicTermsPage() {
  useForceLightTheme();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [signing, setSigning] = useState(false);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/public/jobs/${token}/terms`)
      .then(({ data }) => {
        setData(data);
        setName(data.job?.contact_name || "");
      })
      .catch(() => setError("This link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    setFormError("");
    if (!name.trim()) {
      setFormError("Please type your full name to sign");
      return;
    }
    setSigning(true);
    try {
      const { data: res } = await axios.post(`${API_BASE}/public/jobs/${token}/sign-terms`, {
        signature_name: name.trim(),
      });
      setData((d) => ({
        ...d,
        job: {
          ...d.job,
          terms_signed_at: res.terms_signed_at,
          terms_signed_name: res.terms_signed_name || name.trim(),
        },
      }));
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Something went wrong — try again in a moment.");
    } finally {
      setSigning(false);
    }
  };

  if (error) {
    return (
      <div className="wc-public-light min-h-screen grain flex items-center justify-center px-4 bg-slate-50">
        <div className="glass rounded-3xl p-8 text-slate-700 flex items-center gap-3" data-testid="public-terms-error">
          <AlertTriangle className="w-5 h-5 text-red-500" /> {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="wc-public-light min-h-screen grain flex items-center justify-center px-4 bg-slate-50">
        <div className="glass rounded-3xl p-8 text-slate-700">Loading…</div>
      </div>
    );
  }

  const signed = !!data.job?.terms_signed_at;
  const bizName = data.business?.name || "WrapConsole";

  return (
    <div className="wc-public-light min-h-screen grain px-4 py-16 flex justify-center bg-slate-50 text-slate-900">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-2 mb-8 justify-center">
          {data.business?.logo_url ? (
            <img src={data.business.logo_url} alt={bizName} className="max-h-10 max-w-[180px]" data-testid="public-terms-logo" />
          ) : (
            <>
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl text-white">{bizName}</span>
            </>
          )}
        </div>

        <div className="glass-strong rounded-3xl p-8 md:p-10 space-y-6">
          <div>
            <div className="text-emerald-300 text-[11px] uppercase tracking-widest flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Installation agreement
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tighter text-white mt-1">
              {data.terms?.title || "Installation Terms & Conditions"}
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              For {data.job?.contact_name} · {data.job?.vehicle}
              {data.job?.scheduled_date ? ` · booked ${data.job.scheduled_date}` : ""}
            </p>
          </div>

          <div
            data-testid="public-terms-body"
            className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 max-h-[60vh] overflow-y-auto text-slate-200 text-[15px] leading-relaxed"
          >
            <RenderMarkdown source={data.terms?.body_markdown || ""} />
          </div>

          {signed ? (
            <div
              data-testid="public-terms-signed-view"
              className="bg-emerald-500/[0.08] border border-emerald-500/30 rounded-2xl p-5 text-center space-y-2"
            >
              <div className="mx-auto w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="text-white font-semibold">
                Signed by {data.job.terms_signed_name}
              </div>
              <div className="text-xs text-slate-400">
                {new Date(data.job.terms_signed_at).toLocaleString()}
              </div>
              <div className="text-sm text-slate-300 mt-1">
                Thanks — the workshop can now start your job as scheduled.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1.5">
                  Type your full name to sign
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Alex Morgan"
                  data-testid="public-terms-signature"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-400/50"
                />
                <div className="text-[11px] text-slate-500 mt-1.5">
                  Your electronic signature is legally equivalent to a written one. We'll timestamp it and keep a copy on your job.
                </div>
              </div>

              {formError && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2" data-testid="public-terms-form-error">
                  {formError}
                </div>
              )}

              <button
                type="button"
                onClick={submit}
                disabled={signing}
                data-testid="public-terms-submit"
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-black font-semibold rounded-full py-3.5 hover:bg-emerald-400 transition disabled:opacity-60"
              >
                {signing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</>
                  : <><CheckCircle2 className="w-5 h-5" /> Read &amp; agree to these terms</>}
              </button>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-slate-600 mt-6">
          Powered by <span className="text-slate-400">{bizName}</span>
        </div>
      </div>
    </div>
  );
}
